import React, { Suspense } from "react";
import { commitMutation, graphql } from "react-relay";

import type { ActionItemIntegration } from "~/integrations/actionItemIntegration/index";
import { environment } from "~/models";
import { removeAsanaPerItem } from "~/settings/Integrations/Asana/AsanaIntegration";
import { Button, LoadingSpinner, toast } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";

import { asanaKey } from "../asana";

import type { actionItemUnlinkingModalMutation } from "~/__generated__/actionItemUnlinkingModalMutation.graphql";

import styles from "./css/unlinking.module.css";

const DisconnectActionItemIntegrationLink = graphql`
	mutation actionItemUnlinkingModalMutation($input: DisconnectActionItemIntegrationLinkInput!) {
		disconnectActionItemIntegrationLink(input: $input) {
			ok
		}
	}
`;

interface ActionItemUnlinkingModalProps {
	integration: ActionItemIntegration;

	userId: string;
	blockKey: string;
	noteId: string;
}

function ActionItemUnlinkingModal({
	integration,
	close,
	blockKey,
	noteId,
}: ActionItemUnlinkingModalProps & { close: () => void }) {
	const onSubmit = React.useCallback(() => {
		close();

		if (integration.key === asanaKey) {
			removeAsanaPerItem({ blockKey: blockKey, noteId: noteId, close: close });
		} else {
			commitMutation<actionItemUnlinkingModalMutation>(environment, {
				mutation: DisconnectActionItemIntegrationLink,
				variables: {
					input: {
						noteId,
						blockKey,
					},
				},
				onCompleted: ({ disconnectActionItemIntegrationLink }) => {
					if (disconnectActionItemIntegrationLink?.ok) {
						toast.success(`Disconnected ${integration.displayName}`);
					} else {
						toast.error(`Failed to disconnect ${integration.displayName}`);
					}
				},
			});
		}
	}, [blockKey, close, integration, noteId]);

	return (
		<Modal onClose={close}>
			<Modal.Header noBottomBorder onClose={close}>
				<Modal.Title>
					{integration.logo} {integration.displayName}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body textOnly>
				<Suspense fallback={<LoadingSpinner dimmer={false} />}>
					Are you sure you want to unlink this item?
					<br />
					<p className={styles.helpText}>
						<b>Note: </b>Unlinked items will still exist in {integration.displayName}. Removing the link will simply
						disable syncing.
					</p>
				</Suspense>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="secondary" onClick={close}>
					Cancel
				</Button>
				<Button size="normal" variant="destructive" onClick={onSubmit}>
					Unlink
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export function displayActionItemUnlinkingModal(props: ActionItemUnlinkingModalProps) {
	__globalModalContext?.open(({ close }) => <ActionItemUnlinkingModal {...props} close={close} />);
}
